import React, {
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { FormattedMessage } from 'react-intl';

import { useBind, useSnackbar, useCancellablePromise } from '../../../../../../hooks';
import { getAuthorizationHeader } from '../../../../../../utils/request-headers/get-authorization-header';
import { ConfirmationCodeStepForm } from './confirmation-code-step-form';
import { yup } from '../../../../../../services/yup';
import { oauthApi } from '../../../../../../rest/oauth';
import { useOtpCodeValidation } from '../../../../../../hooks/use-otp-code-validation';
import { useActionsInProgress } from '../../../../../../graphql/preloader/actions/actions-in-progress';
import { IStepperImperativeHandleProps, IStepComponentProps, IFormImperativeHandleProps } from '../../../../../common';
import { IStepsState, TConfirmationCodeValues } from '../../registration.models';
import { IExternalStateProps } from '../../registration.models';
import { RecaptchaActions } from '../../../../../../rest/consumer';
import { ITokenData } from '../../../../../../rest/oauth';
import { useRegistrationError } from '../../hooks/use-registration-error';
import { handleReCaptcha } from '../../../../../../utils';
import { useSmartCaptcha } from '../../../../../../hooks';
import { CaptchaType } from '../../../../../../graphql/configuration';
import { CAPTCHA_CHECK_ERROR } from '../../../../../../constants';

export const ConfirmationCodeStep = forwardRef<
IStepperImperativeHandleProps, IStepComponentProps>(({
  onGoNextSuccess,
  onGoNextFail,
  onGoNext,
  isNavigationAllowed,
  stepsState,
  setStepState,
  incrementStepActionsInProgress,
  decrementStepActionsInProgress,
  externalState,
}, ref): JSX.Element => {
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const stepsStateRegistration = stepsState as IStepsState;

  const {
    makeCancellablePromise,
    CancelledPromiseOnUnmountError,
  } = useCancellablePromise();

  const { enqueueSnackbar } = useSnackbar();
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();

  const incrementStepActionsInProgressBind = useBind(incrementStepActionsInProgress);
  const decrementStepActionsInProgressBind = useBind(decrementStepActionsInProgress);
  const makeCancellablePromiseBind = useBind(makeCancellablePromise);
  const CancelledPromiseOnUnmountErrorBind = useBind(CancelledPromiseOnUnmountError);
  const stepsStateBind = useBind(stepsStateRegistration);
  const setStepStateBind = useBind(setStepState);
  const enqueueSnackbarBind = useBind(enqueueSnackbar);
  const onGoNextFailBind = useBind(onGoNextFail);
  const onGoNextSuccessBind = useBind(onGoNextSuccess);
  const isNavigationAllowedBind = useBind(isNavigationAllowed);
  const onGoNextBind = useBind(onGoNext);
  const addActionInProgressBind = useBind(addActionInProgress);
  const removeActionInProgressBind = useBind(removeActionInProgress);

  const { time, start, reset, status: timerStatus, captcha } = externalState as IExternalStateProps;

  const handleAfterRequestCode = ( token: ITokenData) => {
    removeActionInProgressBind.current();

    setStepStateBind.current!('phone', {
      ...stepsStateBind.current.phone,
      token,
    });

    reset();
    start();

    decrementStepActionsInProgressBind.current!();
  };

  const handlePhoneSubmit = useCallback(async (captchaToken: string, captchaType?: CaptchaType) => {
    const { data: token } = await makeCancellablePromiseBind.current(
      oauthApi.registerPhoneNumberCaptcha({
        phoneNumber: stepsStateBind.current.phone.formValues.phoneNumber,
      }, captchaToken, captchaType),
    );

    handleAfterRequestCode(token);
  }, [makeCancellablePromiseBind, stepsStateBind, handleAfterRequestCode]);

  // SmartCaptcha
  const { smartToken, isVisible, handleSmartCaptcha, showCaptcha, clearToken, isReset, handleReset, hideCaptcha } = useSmartCaptcha();

  // This function is called when the SmartCaptcha challenge is hidden (either passed or closed)
  const handleChallengeHidden = useCallback((): void => {
    if (!smartToken) {
      clearToken();
      hideCaptcha();
    }
  }, [smartToken]);

  const { handleError } = useRegistrationError();

  const handleSubmitError = useCallback((error: any): void => {
    removeActionInProgressBind.current();

    if (error instanceof CancelledPromiseOnUnmountErrorBind.current) {
      return;
    }

    handleError(error);
    decrementStepActionsInProgressBind.current!();
  }, [removeActionInProgressBind, handleError, decrementStepActionsInProgressBind, CancelledPromiseOnUnmountErrorBind]);

  const handleRequestCode = useCallback(async () => {
    if (!isNavigationAllowedBind.current) {
      return;
    }

    // SmartCaptcha - Invisible mode
    if (captcha && captcha?.isEnabled && captcha.type === CaptchaType.smartCaptcha && !captcha.isVisibleCaptcha) {
      showCaptcha(async (token: string) => {
        incrementStepActionsInProgressBind.current!();
        addActionInProgressBind.current();

        try {
          await handlePhoneSubmit(token, captcha.type);
          clearToken();
          handleReset(true);
        } catch (error: any) {
          handleSubmitError(error);
          handleReset(true);
        }
      });

      return;
    }

    // Smart Captcha - Visible mode
    if (captcha && captcha?.isEnabled && captcha.type === CaptchaType.smartCaptcha && captcha.isVisibleCaptcha) {
      if (!smartToken) {
        enqueueSnackbarBind.current(CAPTCHA_CHECK_ERROR, { variant: 'error' });
        return;
      }

      incrementStepActionsInProgressBind.current!();
      addActionInProgressBind.current();

      try {
        await handlePhoneSubmit(smartToken, captcha.type);
        clearToken();
        handleReset(true);
      } catch (error: any) {
        handleSubmitError(error);
        handleReset(true);
      }

      return;
    }

    // For non-SmartCaptcha flows, proceed normally
    incrementStepActionsInProgressBind.current!();
    addActionInProgressBind.current();

    try {
      // reCaptcha
      if (captcha && captcha.isEnabled && captcha.type === CaptchaType.reCaptcha) {
        handleReCaptcha(
          (captchaToken: any) => handlePhoneSubmit(captchaToken, captcha.type),
          handleSubmitError,
          RecaptchaActions.registration,
        );
      } else {
        const { data: token } = await makeCancellablePromiseBind.current(
          oauthApi.registerPhoneNumber({
            phoneNumber: stepsStateBind.current.phone.formValues.phoneNumber,
          }),
        );

        handleAfterRequestCode(token);
      }
    } catch (error: any) {
      handleSubmitError(error);
    }
  }, [
    setStepStateBind,
    stepsStateBind,
    makeCancellablePromiseBind,
    incrementStepActionsInProgressBind,
    isNavigationAllowedBind,
    addActionInProgressBind,
    captcha,
    showCaptcha,
    handleAfterRequestCode,
    handleSubmitError,
    handlePhoneSubmit,
    smartToken,
    clearToken,
    enqueueSnackbarBind,
    handleReset,
  ]);

  const otpCodeValidation = useOtpCodeValidation();

  const validationSchema = useMemo(() => yup.object({
    otpCode: otpCodeValidation,
  }), [otpCodeValidation]);

  useImperativeHandle(ref, () => ({
    goNext() {
      formRef.current!.submit();
    },
    goBack() {},
  }));

  const handleSubmit = useCallback(async (formValues: TConfirmationCodeValues) => {
    if (!isNavigationAllowedBind.current) {
      return;
    }

    onGoNextBind.current();
    addActionInProgressBind.current();

    try {
      await makeCancellablePromiseBind.current(
        oauthApi.confirmPhoneNumber({
          phoneNumber: stepsStateBind.current.phone.formValues.phoneNumber,
          otpCode: formValues.otpCode,
        }, {
          headers: {
            Authorization: getAuthorizationHeader(
              stepsStateBind.current.phone.token.access_token,
            ),
          },
        }),
      );

      removeActionInProgressBind.current();
      onGoNextSuccessBind.current(formValues);
    } catch (error: any) {
      removeActionInProgressBind.current();

      if (error instanceof CancelledPromiseOnUnmountErrorBind.current) {
        return;
      }

      if (error.response) {
        const { status } = error.response;

        switch (status) {
          case 400:
            enqueueSnackbarBind.current(
              <FormattedMessage
                id="common.errors.otpCode.invalid"
              />,
              { variant: 'error' },
            );
            break;
          default:
            break;
        }
      }

      onGoNextFailBind.current();
    }
  }, [
    stepsStateBind,
    enqueueSnackbarBind,
    onGoNextFailBind,
    onGoNextSuccessBind,
    onGoNextBind,
    isNavigationAllowedBind,
    makeCancellablePromiseBind,
    CancelledPromiseOnUnmountErrorBind,
    addActionInProgressBind,
    removeActionInProgressBind,
  ]);

  const handleClearCode = () => {
    if ((stepsState as IStepsState).name) {
      formRef.current!.setValue('otpCode', '');
    }
  };

  const defaultValues = useMemo(
    () => stepsStateRegistration.confirmationCode || {
      otpCode: '',
    },
    [stepsStateRegistration.confirmationCode],
  );

  return (
    <ConfirmationCodeStepForm
      ref={formRef}
      onRequestCode={handleRequestCode}
      requestCodeTime={time}
      timerStatus={timerStatus}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      onClearCode={handleClearCode}
      captchaType={captcha?.type}
      onSuccess={handleSmartCaptcha}
      isVisible={isVisible}
      captcha={captcha}
      isResetCaptcha={isReset}
      onChallengeHidden={handleChallengeHidden}
    />
  );
});
